import { ArrowRight, BarChart3, Shield, Sparkles, Target, TrendingUp, Zap } from 'lucide-react';
import { useEffect } from 'react';
import { InView } from 'react-intersection-observer';
import { Link, useLocation } from 'react-router-dom';
import Footer from './footer';
import Header from './header';
import './landing.css';
import useScrollBehavior from './useScrollBehavior';

const features = [
    {
        icon: Shield,
        title: 'Source-Based and Transparent',
        description:
            'We collect data directly from original company materials - reports, press releases, presentations, and supplemental disclosures - and link every data point to its source with accessible screenshots.',
    },
    {
        icon: Target,
        title: 'Company-Specific KPIs',
        description:
            "Instead of applying a one-size-fits-all model, we track standard financial metrics together with company-specific KPIs that capture each business's individual performance drivers.",
    },
    {
        icon: Zap,
        title: 'Rigorous Validation',
        description:
            'All data undergoes multiple verification stages, combining automated checks with expert human oversight to ensure reliability.',
    },
    {
        icon: TrendingUp,
        title: 'Bottom-Up Structuring',
        description:
            'Data is organized for clarity and insight - not conformity. We avoid generic templates to preserve the nuance behind the numbers.',
    },
    {
        icon: BarChart3,
        title: 'Accurate Calculations',
        description:
            "Our calculations are always transparent, grounded in the company's own definitions whenever available.",
    },
    {
        icon: Sparkles,
        title: 'Full Customization',
        description:
            'Users can filter, categorize, and view the data according to their own analytical needs with personal estimates.',
    },
];

const INTERSECTION_THRESHOLD = 0.25;

export default function LandingPage() {
    const location = useLocation();
    const { scrollToElement, scrollToTop } = useScrollBehavior();

    /**
     * Double requestAnimationFrame ensures reliable scrolling after navigation.
     * Why we need this:
     *  When React Router navigates to a new page, several things happen in sequence:
     *  1. React updates the virtual DOM
     *  2. Browser updates the actual DOM
     *  3. Browser paints the new DOM to the screen (Frame N+1)
     *  4. Browser finalizes layout calculations (Frame N+2)
     *
     * If we try to scroll immediately, the target element might not be properly
     * positioned yet, causing the scroll to fail or jump to the wrong location.
     *
     * Each requestAnimationFrame waits for the next browser "frame" (screen refresh).
     * Browsers typically refresh at 60fps, meaning each frame is ~16ms apart.
     *
     * - 1st rAF: Waits for the next frame (Frame N+1) - DOM is painted
     * - 2nd rAF: Waits for one more frame (Frame N+2) - Layout is stable
     */
    useEffect(() => {
        const id = location.hash.replace('#', '');
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                let hasScrolled = false;
                if (id) {
                    hasScrolled = scrollToElement(id);
                }

                if (!id || !hasScrolled) {
                    scrollToTop();
                }
            });
        });
    }, [location.hash, scrollToElement, scrollToTop]);

    return (
        <div id="app">
            <Header />
            <main id="main-content">
                <section id="hero" className="landing__hero">
                    <div className="sp-container-md">
                        <div className="landing__hero-content">
                            <div className="landing__badge">
                                <Zap className="sp-icon-sm" />
                                Built for Professional Analysts
                            </div>

                            <h1 className="landing__hero-title">
                                Company-Defined Numbers,
                                <br />
                                <span className="landing__highlight">Intelligently Structured</span>
                            </h1>

                            <p className="landing__hero-description">
                                Built for investors who care about what is actually reported. We preserve
                                company-specific insights that standardized models overlook.
                            </p>

                            <div className="landing__hero-buttons">
                                <Link
                                    to="/companies"
                                    className="sp-btn sp-btn--primary sp-btn--lg"
                                >
                                    Start your 3D Journey
                                    <ArrowRight className="sp-icon-md" />
                                </Link>
                                <Link
                                    to="/book-demo"
                                    className="sp-btn sp-btn--outline sp-btn--lg"
                                >
                                    Book Demo
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>

                <InView triggerOnce threshold={INTERSECTION_THRESHOLD}>
                    {({ inView, ref }) => (
                        <section
                            id="features"
                            ref={ref}
                            className={`landing__features fadeInUp ${
                                inView ? 'fadeInUp--visible' : 'fadeInUp--hidden'
                            }`}
                        >
                            <div className="sp-container-md">
                                <div className="sp-section-header">
                                    <h2>The Problem We Solve</h2>
                                    <div className="sp-divider"></div>
                                </div>

                                <div className="sp-card sp-card--primary">
                                    <div className="sp-card__header">
                                        <Target className="sp-icon-md" />
                                        Beyond Standardized Analysis
                                    </div>
                                    <div className="sp-card__content">
                                        <p>
                                            Traditional investing tools often adjust and rearrange financial statement
                                            numbers to make companies comparable. While this approach has its
                                            advantages, it comes with a significant drawback:
                                        </p>
                                        <div className="sp-callout">
                                            <p>Company-specific facts and details get lost in the process</p>
                                        </div>
                                        <p>
                                            3D StockPicker was created to solve this problem—preserving and analyzing
                                            critical company-specific insights that standardized models overlook. The
                                            result: a deeper, more nuanced understanding of investment opportunities.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </section>
                    )}
                </InView>

                <InView triggerOnce threshold={INTERSECTION_THRESHOLD}>
                    {({ inView, ref }) => (
                        <section
                            id="approach"
                            ref={ref}
                            className={`landing__approach fadeInUp ${
                                inView ? 'fadeInUp--visible' : 'fadeInUp--hidden'
                            }`}
                        >
                            <div className="sp-container-lg">
                                <div className="sp-section-header">
                                    <h2>Our Unique Approach</h2>
                                    <div className="sp-divider"></div>
                                    <p className="sp-section-description">
                                        Our bottom-up process starts with company disclosures, preserving original
                                        definitions and adapting to changes for consistent, accurate data.
                                    </p>
                                </div>

                                <div className="landing__feature-grid">
                                    {features.map((feature) => (
                                        <div key={feature.title} className="landing__feature-card">
                                            <div className="landing__feature-header">
                                                <div className="landing__feature-icon">
                                                    <feature.icon className="sp-icon-md" />
                                                </div>
                                                <h3>{feature.title}</h3>
                                            </div>
                                            <p>{feature.description}</p>
                                        </div>
                                    ))}
                                </div>

                                <div className="landing__approach-footer">
                                    <p>This is not just data - it's context, precision, and control in one platform.</p>
                                </div>
                            </div>
                        </section>
                    )}
                </InView>

                <section className="landing__cta fadeInUp">
                    <div className="sp-container-sm">
                        <h2>Ready to transform your analysis?</h2>
                        <p>Join 3D StockPicker today and discover a new dimension of financial insights.</p>
                        <Link
                            to="/companies"
                            className="sp-btn sp-btn--primary sp-btn--lg"
                        >
                            Start your 3D Journey
                            <ArrowRight className="sp-icon-md" />
                        </Link>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
